import type { HTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/utils'

const spacing = {
    none: '',
    sm: 'py-10 md:py-14',
    md: 'py-16 md:py-24',
    lg: 'py-24 md:py-36'
} as const

const tones = {
    paper: 'bg-paper text-ink',
    surface: 'bg-surface text-ink',
    sunken: 'bg-surface-2 text-ink',
    /* The one dark band. Use it to mark a change of pace, never at random. */
    ink: 'bg-surface-inverse text-ink-inverse'
} as const

interface SectionProps extends HTMLAttributes<HTMLElement> {
    /** Stable, human-readable anchor id. Required so sections can be linked. */
    id: string
    size?: keyof typeof spacing
    tone?: keyof typeof tones
    /** Hairline rule along the top edge, for quiet separation without a colour change. */
    divider?: boolean
    children: ReactNode
}

export const Section = ({
    id,
    size = 'md',
    tone = 'paper',
    divider = false,
    className,
    children,
    ...props
}: SectionProps) => {
    return (
        <section
            id={id}
            className={cn(
                'relative w-full',
                spacing[size],
                tones[tone],
                divider && 'border-t border-line',
                className
            )}
            {...props}
        >
            {children}
        </section>
    )
}

interface SectionHeaderProps {
    title: ReactNode
    description?: ReactNode
    /** A link or button that sits opposite the title on wide screens. */
    action?: ReactNode
    align?: 'start' | 'center'
    className?: string
}

export const SectionHeader = ({
    title,
    description,
    action,
    align = 'start',
    className
}: SectionHeaderProps) => {
    const centered = align === 'center'

    return (
        <div
            className={cn(
                'flex gap-6',
                centered
                    ? 'flex-col items-center text-center'
                    : 'flex-col md:flex-row md:items-end md:justify-between',
                className
            )}
        >
            <div className={cn('flex flex-col gap-3', centered ? 'items-center' : 'max-w-2xl')}>
                <h2 className='font-display text-h2 text-balance'>{title}</h2>
                {description && (
                    <p className={cn('text-lead text-ink-muted text-pretty', centered && 'max-w-xl')}>
                        {description}
                    </p>
                )}
            </div>
            {action && <div className='shrink-0'>{action}</div>}
        </div>
    )
}
